import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faTimes } from '@fortawesome/free-solid-svg-icons';
import Header from './component/Header';
import Switch from 'react-switch';
import { API_URL } from '../config';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import AddPlatePopup from './component/AddPlatePopup';
import ChangePreferredContactPopup from './component/ChangePreferredContactPopup';
import ChangePreferredPaymentMethodPopup from './component/ChangePreferredPaymentMethodPopup';
import PublicFooter from '../components/layout/PublicFooter';
import { Link } from 'react-router-dom';

const StatCard = ({ label, value, icon, color }) => {
  const colorMap = {
    blue: 'tw-bg-blue-50 tw-text-blue-600',
    green: 'tw-bg-green-50 tw-text-green-600',
    purple: 'tw-bg-purple-50 tw-text-purple-600',
    amber: 'tw-bg-amber-50 tw-text-amber-600',
  };
  return (
    <div className="tw-bg-white tw-rounded-xl tw-border tw-border-gray-200 tw-shadow-sm tw-p-5 tw-flex tw-items-center tw-gap-4">
      <div className={`tw-w-12 tw-h-12 tw-rounded-lg tw-flex tw-items-center tw-justify-center ${colorMap[color]}`}>
        {icon}
      </div>
      <div>
        <p className="tw-text-sm tw-text-gray-500 tw-m-0">{label}</p>
        <p className="tw-text-2xl tw-font-bold tw-text-gray-900 tw-m-0">{value}</p>
      </div>
    </div>
  );
};

const Account = () => {
  const [plateRenewals, setPlateRenewals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [flashMessage, setFlashMessage] = useState('');
  const [isAddPlateModalOpen, setIsAddPlateModalOpen] = useState(false);
  const [isPreferredContactModalOpen, setIsPreferredContactModalOpen] = useState(false);
  const [isPreferredPaymentMethodModalOpen, setIsPreferredPaymentMethodModalOpen] = useState(false);
  const [selectedPlateId, setSelectedPlateId] = useState(null);

  const openPreferredContactModal = (plateId) => {
    setSelectedPlateId(plateId);
    setIsPreferredContactModalOpen(true);
  };

  const openPreferredPaymentMethodModal = (plateId) => {
    setSelectedPlateId(plateId);
    setIsPreferredPaymentMethodModalOpen(true);
  };

  useEffect(() => {
    const storedFlashMessage = localStorage.getItem('flashMessage');
    if (storedFlashMessage) {
      setFlashMessage(storedFlashMessage);
      localStorage.removeItem('flashMessage');
    }
  }, []);

  useEffect(() => {
    loadPlateRenewalData();
  }, []);

  const loadPlateRenewalData = () => {
    const authToken = localStorage.getItem('token');
    if (!authToken) return;

    fetch(API_URL + '/plate-renewals', {
      headers: { Authorization: `Bearer ${authToken}` },
    })
      .then((response) => response.json())
      .then((data) => {
        setPlateRenewals(data);
        setLoading(false);
      })
      .catch((error) => {
        console.error('Error fetching data:', error);
        setLoading(false);
      });
  };

  const hideFlashMessage = () => setFlashMessage('');

  const toggleStatus = (index) => {
    const updatedPlateRenewals = [...plateRenewals];
    const plate = updatedPlateRenewals[index];
    plate.is_active = !plate.is_active;

    const authToken = localStorage.getItem('token');
    const renewalId = plate.id;
    const newStatusMessage = plate.is_active
      ? 'Auto renewal of plate activated.'
      : 'Auto renewal of plate deactivated.';

    fetch(API_URL + `/plate-renewals/${renewalId}/change-status`, {
      method: 'PUT',
      headers: {
        Authorization: `Bearer ${authToken}`,
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
      body: JSON.stringify({ is_active: plate.is_active, type: 'plate' }),
    })
      .then((response) => {
        if (!response.ok) throw new Error('API request failed');
        toast.success(newStatusMessage, { autoClose: 3000 });
      })
      .catch((error) => {
        console.error('Error updating status:', error);
        toast.error('Failed to update status', { autoClose: 3000 });
      });

    setPlateRenewals(updatedPlateRenewals);
  };

  const openAddPlateModal = () => setIsAddPlateModalOpen(true);
  const closeAddPlateModal = () => setIsAddPlateModalOpen(false);
  const ClosePreferredContactModal = () => setIsPreferredContactModalOpen(false);
  const ClosePreferredPaymentMethodModal = () => setIsPreferredPaymentMethodModalOpen(false);

  const activePlateRenewals = plateRenewals.filter((renewal) => renewal).length;

  return (
    <div className="tw-min-h-screen tw-flex tw-flex-col tw-bg-gray-50">
      <Header />

      <div className="tw-flex-1 tw-max-w-7xl tw-mx-auto tw-w-full tw-px-4 sm:tw-px-6 lg:tw-px-8 tw-py-8">
        {/* Flash message */}
        {flashMessage && (
          <div className="tw-mb-6 tw-bg-green-50 tw-border tw-border-green-200 tw-rounded-lg tw-px-4 tw-py-3 tw-flex tw-items-center tw-justify-between">
            <span className="tw-text-sm tw-text-green-800">{flashMessage}</span>
            <button onClick={hideFlashMessage} className="tw-text-green-600 hover:tw-text-green-800 tw-border-0 tw-bg-transparent tw-cursor-pointer">
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>
        )}

        {/* Page header */}
        <div className="tw-mb-8">
          <h1 className="tw-text-2xl tw-font-bold tw-text-gray-900 tw-m-0">Dashboard</h1>
          <p className="tw-text-sm tw-text-gray-500 tw-mt-1 tw-m-0">Manage your renewals and account</p>
        </div>

        {/* Stat cards */}
        <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 lg:tw-grid-cols-4 tw-gap-4 tw-mb-8">
          <StatCard
            label="Total Plates"
            value={activePlateRenewals}
            color="blue"
            icon={<svg className="tw-w-6 tw-h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M15 9h3.75M15 12h3.75M15 15h3.75M4.5 19.5h15a2.25 2.25 0 002.25-2.25V6.75A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25v10.5A2.25 2.25 0 004.5 19.5zm6-10.125a1.875 1.875 0 11-3.75 0 1.875 1.875 0 013.75 0zm-3.375 6.375h4.5" /></svg>}
          />
          <StatCard
            label="Total Properties"
            value={0}
            color="green"
            icon={<svg className="tw-w-6 tw-h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M2.25 12l8.954-8.955a1.126 1.126 0 011.591 0l8.955 8.955M4.5 9.75v10.125c0 .621.504 1.125 1.125 1.125H9.75v-4.875c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21h4.125c.621 0 1.125-.504 1.125-1.125V9.75" /></svg>}
          />
          <StatCard
            label="Total Licenses"
            value={0}
            color="purple"
            icon={<svg className="tw-w-6 tw-h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M15 9h3.75M15 12h3.75M15 15h3.75M4.5 19.5h15a2.25 2.25 0 002.25-2.25V6.75A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25v10.5A2.25 2.25 0 004.5 19.5z" /></svg>}
          />
          <StatCard
            label="Total Utilities"
            value={0}
            color="amber"
            icon={<svg className="tw-w-6 tw-h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M3.75 13.5l10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75z" /></svg>}
          />
        </div>

        {/* Plate renewals table */}
        <div className="tw-bg-white tw-rounded-xl tw-border tw-border-gray-200 tw-shadow-sm">
          <div className="tw-flex tw-items-center tw-justify-between tw-px-6 tw-py-4 tw-border-b tw-border-gray-200">
            <h2 className="tw-text-lg tw-font-semibold tw-text-gray-900 tw-m-0">License Plate Information</h2>
            <button
              onClick={openAddPlateModal}
              className="tw-inline-flex tw-items-center tw-gap-2 tw-bg-blue-600 hover:tw-bg-blue-700 tw-text-white tw-text-sm tw-font-medium tw-px-4 tw-py-2 tw-rounded-lg tw-border-0 tw-cursor-pointer tw-transition-colors"
            >
              <FontAwesomeIcon icon={faPlus} />
              Add Plate
            </button>
          </div>

          <div className="tw-overflow-x-auto">
            <table className="tw-w-full">
              <thead>
                <tr className="tw-border-b tw-border-gray-200 tw-bg-gray-50">
                  <th className="tw-text-left tw-px-6 tw-py-3 tw-text-xs tw-font-semibold tw-text-gray-500 tw-uppercase tw-tracking-wider">Plate Number</th>
                  <th className="tw-text-left tw-px-6 tw-py-3 tw-text-xs tw-font-semibold tw-text-gray-500 tw-uppercase tw-tracking-wider">County</th>
                  <th className="tw-text-left tw-px-6 tw-py-3 tw-text-xs tw-font-semibold tw-text-gray-500 tw-uppercase tw-tracking-wider">Days Until Renewal</th>
                  <th className="tw-text-left tw-px-6 tw-py-3 tw-text-xs tw-font-semibold tw-text-gray-500 tw-uppercase tw-tracking-wider">Preferred Contact</th>
                  <th className="tw-text-left tw-px-6 tw-py-3 tw-text-xs tw-font-semibold tw-text-gray-500 tw-uppercase tw-tracking-wider">Payment Method</th>
                  <th className="tw-text-left tw-px-6 tw-py-3 tw-text-xs tw-font-semibold tw-text-gray-500 tw-uppercase tw-tracking-wider">Status</th>
                </tr>
              </thead>
              <tbody className="tw-divide-y tw-divide-gray-100">
                {loading ? (
                  <tr>
                    <td colSpan="6" className="tw-px-6 tw-py-12 tw-text-center tw-text-sm tw-text-gray-500">
                      <div className="tw-flex tw-items-center tw-justify-center tw-gap-2">
                        <svg className="tw-animate-spin tw-w-5 tw-h-5 tw-text-blue-500" fill="none" viewBox="0 0 24 24">
                          <circle className="tw-opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="tw-opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                        </svg>
                        Loading...
                      </div>
                    </td>
                  </tr>
                ) : plateRenewals.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="tw-px-6 tw-py-12 tw-text-center">
                      <div className="tw-flex tw-flex-col tw-items-center tw-gap-3">
                        <div className="tw-w-12 tw-h-12 tw-rounded-full tw-bg-gray-100 tw-flex tw-items-center tw-justify-center">
                          <svg className="tw-w-6 tw-h-6 tw-text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M15 9h3.75M15 12h3.75M15 15h3.75M4.5 19.5h15a2.25 2.25 0 002.25-2.25V6.75A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25v10.5A2.25 2.25 0 004.5 19.5zm6-10.125a1.875 1.875 0 11-3.75 0 1.875 1.875 0 013.75 0zm-3.375 6.375h4.5" /></svg>
                        </div>
                        <p className="tw-text-sm tw-text-gray-500 tw-m-0">No plates added yet</p>
                        <button
                          onClick={openAddPlateModal}
                          className="tw-text-sm tw-text-blue-600 hover:tw-text-blue-700 tw-font-medium tw-border-0 tw-bg-transparent tw-cursor-pointer"
                        >
                          Add your first plate
                        </button>
                      </div>
                    </td>
                  </tr>
                ) : (
                  plateRenewals.map((data, index) => (
                    <tr key={index} className="hover:tw-bg-gray-50 tw-transition-colors">
                      <td className="tw-px-6 tw-py-4 tw-text-sm tw-font-medium">
                        <Link to={"/renewal-history/" + data.plate_number} className="tw-text-blue-600 hover:tw-text-blue-700 tw-no-underline hover:tw-underline">
                          {data.plate_number}
                        </Link>
                      </td>
                      <td className="tw-px-6 tw-py-4 tw-text-sm tw-text-gray-700">
                        {data.municipality_name}, {data.state_name}
                      </td>
                      <td className="tw-px-6 tw-py-4 tw-text-sm tw-text-gray-700">
                        {data.expiration_date}
                      </td>
                      <td className="tw-px-6 tw-py-4 tw-text-sm tw-text-gray-700">
                        <span>{data.notificationMethod}</span>
                        <button
                          onClick={() => openPreferredContactModal(data.id)}
                          className="tw-ml-1.5 tw-text-xs tw-text-blue-600 hover:tw-text-blue-700 tw-border-0 tw-bg-transparent tw-cursor-pointer"
                        >
                          Change
                        </button>
                      </td>
                      <td className="tw-px-6 tw-py-4 tw-text-sm tw-text-gray-700">
                        <span>{data.paymentMethod}</span>
                        <button
                          onClick={() => openPreferredPaymentMethodModal(data.id)}
                          className="tw-ml-1.5 tw-text-xs tw-text-blue-600 hover:tw-text-blue-700 tw-border-0 tw-bg-transparent tw-cursor-pointer"
                        >
                          Change
                        </button>
                      </td>
                      <td className="tw-px-6 tw-py-4">
                        <Switch
                          onChange={() => toggleStatus(index)}
                          checked={Boolean(data.is_active)}
                          onColor="#16a34a"
                          offColor="#d1d5db"
                          width={44}
                          height={24}
                          handleDiameter={20}
                          uncheckedIcon={false}
                          checkedIcon={false}
                        />
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <AddPlatePopup loadPlateRenewalData={loadPlateRenewalData} isOpen={isAddPlateModalOpen} onRequestClose={closeAddPlateModal} />
      <ChangePreferredContactPopup selectedPlateId={selectedPlateId} loadPlateRenewalData={loadPlateRenewalData} isOpen={isPreferredContactModalOpen} onRequestClose={ClosePreferredContactModal} />
      <ChangePreferredPaymentMethodPopup selectedPlateId={selectedPlateId} loadPlateRenewalData={loadPlateRenewalData} isOpen={isPreferredPaymentMethodModalOpen} onRequestClose={ClosePreferredPaymentMethodModal} />
      <ToastContainer />
      <PublicFooter />
    </div>
  );
};

export default Account;
